import {AvatarStack} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {Thread, Author} from '@github-ui/diff-lines/conversation-types'
import {useMemo} from 'react'

type Props = {
  skipBeforeCommentId?: number | null
  skipAfterCommentId?: number | null
  thread: Thread
}

export function PreviewAuthors({skipBeforeCommentId, skipAfterCommentId, thread}: Props) {
  const {commentAuthors} = useMemo(() => {
    const uniqueAuthors = new Set<string>()
    const commentsArray = thread?.commentsData?.comments ?? []

    // Start collecting immediately if no skipBefore constraint
    let shouldCollect = !skipBeforeCommentId

    // A plain loop (rather than reduce) keeps `shouldCollect` mutated in the loop body instead of a
    // captured lambda, which the React Compiler can analyze without a disable.
    const extractedAuthors: Author[] = []
    for (const comment of commentsArray) {
      // Check if we should start collecting (passed the skipBefore comment)
      if (skipBeforeCommentId && comment.databaseId === skipBeforeCommentId) {
        shouldCollect = true
        continue // skip this comment itself
      }

      // Check if we should stop collecting (reached the skipAfter comment)
      if (skipAfterCommentId && comment.databaseId === skipAfterCommentId) {
        shouldCollect = false
      }

      if (!shouldCollect) continue

      const author = comment.author
      if (!author || !author.avatarUrl) continue

      if (!uniqueAuthors.has(author.avatarUrl)) {
        extractedAuthors.push(author)
        uniqueAuthors.add(author.avatarUrl)
      }
    }

    return {commentAuthors: extractedAuthors}
  }, [skipBeforeCommentId, skipAfterCommentId, thread?.commentsData?.comments])

  // If no authors and no label, render nothing
  if (commentAuthors.length < 1) return null

  return (
    <AvatarStack>
      {commentAuthors.map(({login, avatarUrl}) => (
        <GitHubAvatar key={avatarUrl} alt={login} size={18} src={avatarUrl} />
      ))}
    </AvatarStack>
  )
}
